import type { Asset, ReservationRequest, V2Task } from "@/v2/shared/model";

// CB-17: vaste, hardcoded takenlijsten per niveau. Niet beheerbaar in de MVP;
// de definitieve lijsten worden met PZA vastgelegd.
export const REQUEST_TASK_LABELS: ReadonlyArray<string> = [
  "Volledigheid en kwaliteit controleren (plaatsbezoek-foto's, motivatie)",
  "Juridische conformiteit en machtiging nakijken",
  "Locatie beoordelen (bestaande stadscamera/preset, paal aanwezig)",
];

export const DOSSIER_TASK_LABELS: ReadonlyArray<string> = [
  "Periode en capaciteit bevestigen (asset zoeken of wachtrij opvolgen)",
  "Aanvrager informeren over de planning",
  "Einddatum en eventuele verlenging opvolgen",
];

export const DEPLOYMENT_TASK_LABELS: ReadonlyArray<string> = [
  "Plaatsing afspreken met de externe firma",
  "Effectieve plaatsing registreren en werking/kijkrichting controleren",
  "Ophaling inplannen en registreren",
];


// Herinnering (CB-17) op een geseede taak zetten, optioneel gericht aan één
// beheerder; zonder persoon geldt de melding voor het hele team.
function withReminder(
  tasks: Array<V2Task>,
  index: number,
  reminderDate: string,
  reminderTime?: string,
  reminderAssigneeId?: string,
): Array<V2Task> {
  return tasks.map((t, i) =>
    i === index ? { ...t, reminderDate, reminderTime, reminderAssigneeId } : t,
  );
}

function seedTasks(
  idPrefix: string,
  labels: ReadonlyArray<string>,
  doneCount: number,
  doneBy: string,
  doneAt: string,
): Array<V2Task> {
  return labels.map((label, i) => ({
    id: `${idPrefix}-${i + 1}`,
    label,
    done: i < doneCount,
    doneBy: i < doneCount ? doneBy : undefined,
    doneAt: i < doneCount ? doneAt : undefined,
  }));
}

// Vloot — opgeladen via Excel (CB-29); hier gemockt. 3 vaste types,
// nummer blijft actief bij fysieke vervanging.
export const SEED_ASSETS: ReadonlyArray<Asset> = [
  // "In gebruik" wordt berekend uit de inzetten; opgeslagen wordt enkel de
  // manuele toestand (operationeel / buiten werking / in onderhoud).
  { id: "a-001", number: "CAM-001", type: "msp", status: "AVAILABLE", comments: [] },
  { id: "a-002", number: "CAM-002", type: "msp", status: "AVAILABLE", comments: [] },
  { id: "a-003", number: "CAM-003", type: "msp", status: "AVAILABLE", comments: [] },
  { id: "a-004", number: "CAM-004", type: "mobileTree", status: "AVAILABLE", comments: [] },
  { id: "a-005", number: "CAM-005", type: "mobileTree", status: "AVAILABLE", comments: [] },
  {
    id: "a-006",
    number: "CAM-006",
    type: "mobileTree",
    status: "IN_MAINTENANCE",
    comments: [
      {
        id: "ac-1",
        author: "Lien Vandevelde",
        createdAt: "2026-06-24T09:00:00.000Z",
        text: "Lens beschadigd bij ophaling, hersteld tegen half juli.",
        kind: "note",
      },
    ],
  },
  { id: "a-007", number: "CAM-007", type: "camcar", status: "AVAILABLE", comments: [] },
  { id: "a-008", number: "CAM-008", type: "camcar", status: "OUT_OF_ORDER", comments: [] },
  { id: "a-009", number: "CAM-009", type: "camtower", status: "AVAILABLE", comments: [] },
  { id: "a-010", number: "CAM-010", type: "camtower", status: "AVAILABLE", comments: [] },
];

const REQUESTER = {
  requesterId: "u-maarten",
  requesterLastName: "Hendrickx",
  requesterFirstName: "Maarten",
  requesterEmail: "maarten.hendrickx@police.belgium.eu",
  requesterPhone: "+32 471 12 34 56",
  requesterService: "Wijkteam Antwerpen-Noord",
  requesterServiceContact: "wijkteam.noord@police.belgium.eu",
};

export const SEED_REQUESTS: ReadonlyArray<ReservationRequest> = [
  {
    id: "r-9201",
    number: "CB-2026-9201",
    title: "Cameratoezicht sluikstort Dambruggestraat",
    status: "ACCEPTED",
    ...REQUESTER,
    startDate: "2026-07-06",
    startTime: "08:00",
    endDate: "2026-08-06",
    extension: "possible",
    address: "Dambruggestraat 120, 2060 Antwerpen",
    locationLat: 51.227,
    locationLng: 4.4262,
    stadsdeel: "Antwerpen-Noord",
    locationDescription:
      "Camera op bestaande paal hoek Dambruggestraat/Lange Beeldekensstraat, kijkrichting naar de glasbollen.",
    polePresence: "yes",
    environmentalFactors: "Drukke winkelstraat, veel voetgangers.",
    event: "",
    eventInfo: "",
    parkingSigns: "",
    reason: "Aanhoudend sluikstort aan de glasbollen, klachten van bewoners.",
    pvNumber: "AN.45.LL.012345/2026",
    specialAuthorization: "",
    addedValue: "Identificatie van overlastplegers, geen stadscamera aanwezig.",
    compliance: "yes",
    qualityConfirmed: true,
    handlerId: "u-lien",
    createdAt: "2026-06-20T09:12:00.000Z",
    archived: false,
    dossiers: [
      {
        id: "dos-1",
        requestId: "r-9201",
        startDate: "2026-07-06",
        endDate: "2026-08-06",
        status: "PLANNED",
        extensionCount: 0,
        comments: [],
        tasks: seedTasks("t-dos1", DOSSIER_TASK_LABELS, 2, "Lien Vandevelde", "2026-06-21T10:10:00.000Z"),
        deployments: [
          // Korte onderbreking (CB-16): de camera moet tijdelijk weg voor de
          // wijkkermis en wordt nadien opnieuw ingezet. Zo heeft CAM-001 twee
          // opeenvolgende planningen in de toekomst.
          {
            id: "dep-1",
            dossierId: "dos-1",
            assetId: "a-001",
            expectedStart: "2026-07-06",
            expectedEnd: "2026-07-20",
            externalFirm: "Vermeer Mobiele Camerasystemen BV",
            status: "PLANNED",
            comments: [],
            tasks: seedTasks("t-dep1", DEPLOYMENT_TASK_LABELS, 1, "Lien Vandevelde", "2026-06-22T09:00:00.000Z"),
          },
          {
            id: "dep-5",
            dossierId: "dos-1",
            assetId: "a-001",
            expectedStart: "2026-07-24",
            expectedEnd: "2026-08-06",
            externalFirm: "Vermeer Mobiele Camerasystemen BV",
            status: "PLANNED",
            comments: [
              {
                id: "c-5",
                author: "Lien Vandevelde",
                createdAt: "2026-06-23T11:00:00.000Z",
                text: "Onderbreking 20/07 tot 24/07 voor de wijkkermis, daarna herinzet op dezelfde locatie.",
                kind: "note",
              },
            ],
            tasks: seedTasks("t-dep5", DEPLOYMENT_TASK_LABELS, 0, "Lien Vandevelde", "2026-06-23T11:00:00.000Z"),
          },
        ],
      },
    ],
    comments: [
      {
        id: "c-1",
        author: "Lien Vandevelde",
        createdAt: "2026-06-21T10:00:00.000Z",
        text: "Aanvraag is volledig. Dossier aangemaakt en inzet gepland.",
        kind: "note",
      },
    ],
    tasks: seedTasks("t-req1", REQUEST_TASK_LABELS, 3, "Lien Vandevelde", "2026-06-21T09:50:00.000Z"),
    history: [
      { id: "h-1", at: "2026-06-20T09:12:00.000Z", author: "Maarten Hendrickx", action: "Aanvraag ingediend" },
      { id: "h-2", at: "2026-06-21T10:05:00.000Z", author: "Lien Vandevelde", action: "Aanvraag aanvaard, dossier aangemaakt" },
      { id: "h-3", at: "2026-06-21T10:15:00.000Z", author: "Lien Vandevelde", action: "Inzet gepland met asset CAM-001" },
    ],
    communications: [
      {
        id: "m-1",
        direction: "outgoing",
        subject: "Aanvraag CB-2026-9201 aanvaard",
        body: "Beste Maarten, je aanvraag werd aanvaard en wordt ingepland.",
        at: "2026-06-21T10:06:00.000Z",
        author: "Systeem (automatische mail)",
      },
    ],
    documents: [
      { id: "d-1", filename: "plannetje-dambrugge.pdf", kind: "plan", sizeLabel: "1,2 MB" },
      { id: "d-2", filename: "plaatsbezoek-1.jpg", kind: "siteVisitPhoto", sizeLabel: "2,8 MB" },
    ],
  },
  {
    id: "r-9202",
    number: "CB-2026-9202",
    title: "Observatie inbraken Zurenborg",
    status: "OPEN",
    ...REQUESTER,
    startDate: "2026-07-15",
    endDate: "2026-08-15",
    extension: "fixed",
    address: "Cogels-Osylei 24, 2600 Berchem",
    locationLat: 51.2035,
    locationLng: 4.436,
    stadsdeel: "Berchem",
    locationDescription: "Kijkrichting naar voordeuren oneven zijde.",
    polePresence: "no",
    environmentalFactors: "Beschermd stadsgezicht, discrete plaatsing vereist.",
    event: "",
    eventInfo: "",
    parkingSigns: "Cogels-Osylei 20 tot 28.",
    reason: "Reeks woninginbraken in de buurt.",
    pvNumber: "AN.45.LL.099887/2026",
    specialAuthorization: "",
    addedValue: "",
    compliance: "yes",
    qualityConfirmed: true,
    createdAt: "2026-06-28T14:30:00.000Z",
    archived: false,
    dossiers: [],
    comments: [],
    tasks: seedTasks("t-req2", REQUEST_TASK_LABELS, 0, "", ""),
    history: [
      { id: "h-4", at: "2026-06-28T14:30:00.000Z", author: "Maarten Hendrickx", action: "Aanvraag ingediend" },
    ],
    communications: [],
    documents: [
      { id: "d-3", filename: "plaatsbezoek-zurenborg.jpg", kind: "siteVisitPhoto", sizeLabel: "3,1 MB" },
    ],
  },
  {
    id: "r-9203",
    number: "CB-2026-9203",
    title: "Camera festival Park Spoor Noord",
    status: "OPEN",
    ...REQUESTER,
    startDate: "2026-08-01",
    endDate: "2026-08-04",
    extension: "fixed",
    address: "Park Spoor Noord, 2060 Antwerpen",
    locationLat: 51.2312,
    locationLng: 4.4267,
    stadsdeel: "Antwerpen-Noord",
    locationDescription: "Overzicht hoofdpodium.",
    polePresence: "unsure",
    environmentalFactors: "Opbouw festival vanaf 30/07.",
    event: "Zomer van Antwerpen",
    eventInfo: "Contact: Jan Peeters, jan@zva.be, +32 470 00 11 22.",
    parkingSigns: "",
    reason: "Crowd monitoring tijdens evenement.",
    pvNumber: "",
    specialAuthorization: "",
    addedValue: "",
    compliance: "unanswered",
    qualityConfirmed: true,
    handlerId: "u-lien",
    createdAt: "2026-06-25T11:00:00.000Z",
    archived: false,
    dossiers: [],
    comments: [
      {
        id: "c-2",
        author: "Lien Vandevelde",
        createdAt: "2026-06-26T08:30:00.000Z",
        text: "Kan je het plannetje met exacte kijkrichting toevoegen en de conformiteitsvraag bevestigen?",
        kind: "question",
      },
    ],
    tasks: seedTasks("t-req3", REQUEST_TASK_LABELS, 1, "Lien Vandevelde", "2026-06-26T08:25:00.000Z"),
    history: [
      { id: "h-5", at: "2026-06-25T11:00:00.000Z", author: "Maarten Hendrickx", action: "Aanvraag ingediend" },
      { id: "h-6", at: "2026-06-26T08:30:00.000Z", author: "Lien Vandevelde", action: "Vraag gesteld aan de aanvrager" },
    ],
    communications: [],
    documents: [],
  },
  {
    // Open aanvraag met gevraagde start binnen het 7-dagenvenster:
    // demonstreert de actielijst-regel "Aanvraag nog niet behandeld" (PZAO-30).
    id: "r-9209",
    number: "CB-2026-9209",
    title: "Sluikstort Sint-Bernardsesteenweg Hoboken",
    status: "OPEN",
    ...REQUESTER,
    startDate: "2026-07-01",
    endDate: "2026-07-31",
    extension: "possible",
    address: "Sint-Bernardsesteenweg 320, 2660 Hoboken",
    locationLat: 51.1795,
    locationLng: 4.3762,
    stadsdeel: "Hoboken",
    locationDescription: "Zicht op de glascontainers en het braakliggend hoekperceel.",
    polePresence: "yes",
    environmentalFactors: "",
    event: "",
    eventInfo: "",
    parkingSigns: "",
    reason: "Herhaaldelijk sluikstort naast de glascontainers.",
    pvNumber: "AN.45.LL.112233/2026",
    specialAuthorization: "",
    addedValue: "",
    compliance: "yes",
    qualityConfirmed: true,
    createdAt: "2026-06-27T10:15:00.000Z",
    archived: false,
    dossiers: [],
    comments: [],
    tasks: seedTasks("t-req9", REQUEST_TASK_LABELS, 0, "", ""),
    history: [
      { id: "h-r9209", at: "2026-06-27T10:15:00.000Z", author: "Maarten Hendrickx", action: "Aanvraag ingediend" },
    ],
    communications: [],
    documents: [
      { id: "d-r9209", filename: "plaatsbezoek-sint-bernardse.jpg", kind: "siteVisitPhoto", sizeLabel: "2,4 MB" },
    ],
  },
  {
    id: "r-9204",
    number: "CB-2026-9204",
    title: "Drugsoverlast Statiestraat Borgerhout",
    status: "ACCEPTED",
    ...REQUESTER,
    startDate: "2026-07-02",
    endDate: "2026-09-10",
    extension: "possible",
    address: "Statiestraat 88, 2140 Borgerhout",
    locationLat: 51.2115,
    locationLng: 4.4405,
    stadsdeel: "Borgerhout",
    locationDescription: "Zicht op inrit zijstraat.",
    polePresence: "no",
    environmentalFactors: "",
    event: "",
    eventInfo: "",
    parkingSigns: "",
    reason: "Aanhoudende drugsoverlast.",
    pvNumber: "AN.45.LL.045566/2026",
    specialAuthorization: "BOM-2026-0042",
    addedValue: "",
    compliance: "yes",
    qualityConfirmed: true,
    handlerId: "u-lien",
    createdAt: "2026-06-18T13:00:00.000Z",
    archived: false,
    dossiers: [
      {
        id: "dos-2",
        requestId: "r-9204",
        // Toegekende start binnen het 7-dagenvenster: demonstreert de
        // actielijst-regel "Nog geen inzet gepland" (PZAO-30).
        startDate: "2026-07-02",
        endDate: "2026-09-10",
        status: "ON_WAITLIST",
        extensionCount: 0,
        comments: [
          {
            id: "c-3",
            author: "Lien Vandevelde",
            createdAt: "2026-06-19T09:00:00.000Z",
            text: "Geen type 2 vrij voor de volledige periode, dossier op wachtrij geplaatst.",
            kind: "note",
          },
        ],
        tasks: withReminder(seedTasks("t-dos2", DOSSIER_TASK_LABELS, 0, "", ""), 0, "2026-06-27", undefined, "u-lien"),
        deployments: [],
      },
    ],
    comments: [],
    tasks: seedTasks("t-req4", REQUEST_TASK_LABELS, 3, "Lien Vandevelde", "2026-06-19T08:55:00.000Z"),
    history: [
      { id: "h-7", at: "2026-06-18T13:00:00.000Z", author: "Maarten Hendrickx", action: "Aanvraag ingediend" },
      { id: "h-8", at: "2026-06-19T09:00:00.000Z", author: "Lien Vandevelde", action: "Aanvaard, dossier op wachtrij geplaatst" },
    ],
    communications: [],
    documents: [
      { id: "d-4", filename: "plaatsbezoek-statie.jpg", kind: "siteVisitPhoto", sizeLabel: "2,2 MB" },
    ],
  },
  {
    id: "r-9205",
    number: "CB-2026-9205",
    title: "Vandalisme speeltuin Linkeroever",
    status: "CLOSED",
    ...REQUESTER,
    startDate: "2026-05-01",
    endDate: "2026-06-01",
    extension: "fixed",
    address: "Frederik van Eedenplein, 2050 Antwerpen",
    locationLat: 51.221,
    locationLng: 4.383,
    stadsdeel: "Linkeroever",
    locationDescription: "Overzicht speeltuin.",
    polePresence: "yes",
    environmentalFactors: "",
    event: "",
    eventInfo: "",
    parkingSigns: "",
    reason: "Herhaald vandalisme aan speeltoestellen.",
    pvNumber: "",
    specialAuthorization: "",
    addedValue: "",
    compliance: "yes",
    qualityConfirmed: true,
    handlerId: "u-lien",
    createdAt: "2026-04-22T08:00:00.000Z",
    archived: false,
    dossiers: [
      {
        id: "dos-3",
        requestId: "r-9205",
        startDate: "2026-05-01",
        endDate: "2026-06-01",
        status: "CLOSED",
        extensionCount: 0,
        comments: [],
        tasks: seedTasks("t-dos3", DOSSIER_TASK_LABELS, 3, "Lien Vandevelde", "2026-05-02T09:00:00.000Z"),
        deployments: [
          {
            id: "dep-2",
            dossierId: "dos-3",
            assetId: "a-004",
            expectedStart: "2026-05-01",
            expectedEnd: "2026-06-01",
            externalFirm: "Vermeer Mobiele Camerasystemen BV",
            status: "CLOSED",
            actualStart: "2026-05-01",
            actualEnd: "2026-06-01",
            executedBy: "Jeroen Vermeer",
            comments: [],
            tasks: seedTasks("t-dep2", DEPLOYMENT_TASK_LABELS, 3, "Lien Vandevelde", "2026-06-01T16:00:00.000Z"),
          },
        ],
      },
    ],
    comments: [],
    tasks: seedTasks("t-req5", REQUEST_TASK_LABELS, 3, "Lien Vandevelde", "2026-04-23T09:00:00.000Z"),
    history: [
      { id: "h-9", at: "2026-04-22T08:00:00.000Z", author: "Maarten Hendrickx", action: "Aanvraag ingediend" },
      { id: "h-10", at: "2026-06-02T09:00:00.000Z", author: "Lien Vandevelde", action: "Aanvraag afgesloten" },
    ],
    communications: [],
    documents: [],
  },
  {
    id: "r-9206",
    number: "CB-2026-9206",
    title: "Overlast nachtwinkel De Coninckplein",
    status: "ACCEPTED",
    ...REQUESTER,
    startDate: "2026-06-15",
    endDate: "2026-07-01",
    extension: "possible",
    address: "De Coninckplein 1, 2060 Antwerpen",
    locationLat: 51.2224,
    locationLng: 4.4238,
    stadsdeel: "Antwerpen-Noord",
    locationDescription: "Zicht op pleinhoek en nachtwinkel.",
    polePresence: "yes",
    environmentalFactors: "",
    event: "",
    eventInfo: "",
    parkingSigns: "",
    reason: "Nachtelijke overlast en sluikstort aan de nachtwinkel.",
    pvNumber: "AN.45.LL.077001/2026",
    specialAuthorization: "",
    addedValue: "",
    compliance: "yes",
    qualityConfirmed: true,
    handlerId: "u-lien",
    createdAt: "2026-06-10T09:00:00.000Z",
    archived: false,
    dossiers: [
      {
        id: "dos-4",
        requestId: "r-9206",
        startDate: "2026-06-15",
        // Verlengd met één maand (CB-22): oorspronkelijk tot 01/07.
        endDate: "2026-08-01",
        status: "PLANNED",
        extensionCount: 1,
        // Interne dossier-opmerking (CB-15): enkel zichtbaar voor beheerders.
        comments: [
          {
            id: "c-6",
            author: "Lien Vandevelde",
            createdAt: "2026-06-27T14:10:00.000Z",
            text: "Firma Vermeer gevraagd om de herinzet van 01/07 voor 10u in te plannen, buiten de markturen.",
            kind: "note",
          },
        ],
        // Taak 1 kreeg een herinnering en werd nadien afgevinkt: de melding
        // toont "Afgewerkt" en verdwijnt zodra ze bekeken is (PZAO-26).
        tasks: withReminder(
          seedTasks("t-dos4", DOSSIER_TASK_LABELS, 2, "Lien Vandevelde", "2026-06-12T10:30:00.000Z"),
          0,
          "2026-06-26",
          undefined,
          "u-lien",
        ),
        deployments: [
          {
            id: "dep-3",
            dossierId: "dos-4",
            assetId: "a-002",
            expectedStart: "2026-06-15",
            expectedEnd: "2026-07-01",
            externalFirm: "Vermeer Mobiele Camerasystemen BV",
            status: "DEPLOYED",
            actualStart: "2026-06-15",
            executedBy: "Jeroen Vermeer",
            comments: [],
            tasks: seedTasks("t-dep3", DEPLOYMENT_TASK_LABELS, 2, "Lien Vandevelde", "2026-06-15T11:00:00.000Z"),
          },
          {
            // Herinzet na de verlenging: tweede inzet, ander asset.
            id: "dep-4",
            dossierId: "dos-4",
            assetId: "a-010",
            expectedStart: "2026-07-01",
            expectedEnd: "2026-08-01",
            externalFirm: "Vermeer Mobiele Camerasystemen BV",
            status: "PLANNED",
            comments: [
              {
                id: "c-4",
                author: "Lien Vandevelde",
                createdAt: "2026-06-27T14:00:00.000Z",
                text: "CAM-002 moet naar onderhoud, herinzet met CAM-010 vanaf 01/07.",
                kind: "note",
              },
            ],
            tasks: withReminder(seedTasks("t-dep4", DEPLOYMENT_TASK_LABELS, 0, "", ""), 0, "2026-06-29", "09:00", "u-karim"),
          },
        ],
      },
    ],
    comments: [],
    tasks: seedTasks("t-req6", REQUEST_TASK_LABELS, 3, "Lien Vandevelde", "2026-06-11T10:00:00.000Z"),
    history: [
      { id: "h-11", at: "2026-06-10T09:00:00.000Z", author: "Maarten Hendrickx", action: "Aanvraag ingediend" },
      { id: "h-12", at: "2026-06-12T10:00:00.000Z", author: "Lien Vandevelde", action: "Aanvaard, dossier aangemaakt en inzet gepland met CAM-002" },
      { id: "h-13", at: "2026-06-27T13:55:00.000Z", author: "Lien Vandevelde", action: "Dossier verlengd tot 01/08/2026 (verlenging 1 van 2)" },
      { id: "h-14", at: "2026-06-27T14:05:00.000Z", author: "Lien Vandevelde", action: "Herinzet gepland met CAM-010 vanaf 01/07" },
    ],
    communications: [],
    documents: [
      { id: "d-5", filename: "plaatsbezoek-coninck.jpg", kind: "siteVisitPhoto", sizeLabel: "2,0 MB" },
    ],
  },
  {
    // Derde toekomstige boeking van CAM-001 (na de twee inzetten in dos-1):
    // toont dat een asset meerdere planningen over dossiers heen kan hebben.
    id: "r-9207",
    number: "CB-2026-9207",
    title: "Sluikstort containerpark Merksem",
    status: "ACCEPTED",
    ...REQUESTER,
    startDate: "2026-08-10",
    endDate: "2026-09-10",
    extension: "fixed",
    address: "Vaartkaai 4, 2170 Merksem",
    locationLat: 51.2452,
    locationLng: 4.4429,
    stadsdeel: "Merksem",
    locationDescription: "Zicht op de toegangspoort en de containerzone.",
    polePresence: "no",
    environmentalFactors: "Industriële omgeving, weinig passage na sluitingstijd.",
    event: "",
    eventInfo: "",
    parkingSigns: "",
    reason: "Sluikstort aan de poort van het containerpark buiten de openingsuren.",
    pvNumber: "AN.45.LL.088210/2026",
    specialAuthorization: "",
    addedValue: "Geen vaste camera aanwezig, daders komen 's nachts met aanhangwagens.",
    compliance: "yes",
    qualityConfirmed: true,
    handlerId: "u-lien",
    createdAt: "2026-07-01T08:30:00.000Z",
    archived: false,
    dossiers: [
      {
        id: "dos-5",
        requestId: "r-9207",
        startDate: "2026-08-10",
        endDate: "2026-09-10",
        status: "PLANNED",
        extensionCount: 0,
        comments: [],
        tasks: withReminder(seedTasks("t-dos5", DOSSIER_TASK_LABELS, 1, "Lien Vandevelde", "2026-07-02T10:00:00.000Z"), 2, "2026-07-06"),
        deployments: [
          {
            id: "dep-6",
            dossierId: "dos-5",
            assetId: "a-001",
            expectedStart: "2026-08-10",
            expectedEnd: "2026-09-10",
            externalFirm: "Vermeer Mobiele Camerasystemen BV",
            status: "PLANNED",
            comments: [],
            tasks: seedTasks("t-dep6", DEPLOYMENT_TASK_LABELS, 0, "", ""),
          },
        ],
      },
    ],
    comments: [],
    tasks: seedTasks("t-req7", REQUEST_TASK_LABELS, 3, "Lien Vandevelde", "2026-07-02T09:30:00.000Z"),
    history: [
      { id: "h-15", at: "2026-07-01T08:30:00.000Z", author: "Maarten Hendrickx", action: "Aanvraag ingediend" },
      { id: "h-16", at: "2026-07-02T10:05:00.000Z", author: "Lien Vandevelde", action: "Aanvaard, dossier aangemaakt en inzet gepland met CAM-001" },
    ],
    communications: [],
    documents: [],
  },
  {
    // "Afronden vergeten" (PZAO-39): alle inzetten zijn afgerond maar het
    // dossier is nog niet afgesloten — signaal op het dashboard en in de
    // notificatiebel (PZAO-41). Dossiers sluiten nooit automatisch.
    id: "r-9208",
    number: "CB-2026-9208",
    title: "Sluikstort glascontainers Kioskplaats Hoboken",
    status: "ACCEPTED",
    ...REQUESTER,
    startDate: "2026-05-20",
    endDate: "2026-06-25",
    extension: "fixed",
    address: "Kioskplaats 3, 2660 Hoboken",
    locationLat: 51.1753,
    locationLng: 4.3487,
    stadsdeel: "Hoboken",
    locationDescription: "Zicht op de glascontainers en de hoek met de Lelieplaats.",
    polePresence: "yes",
    environmentalFactors: "",
    event: "",
    eventInfo: "",
    parkingSigns: "",
    reason: "Sluikstort rond de glascontainers, meldingen van de districtsdienst.",
    pvNumber: "AN.45.LL.061204/2026",
    specialAuthorization: "",
    addedValue: "",
    compliance: "yes",
    qualityConfirmed: true,
    handlerId: "u-lien",
    createdAt: "2026-05-12T10:15:00.000Z",
    archived: false,
    dossiers: [
      {
        id: "dos-6",
        requestId: "r-9208",
        startDate: "2026-05-20",
        endDate: "2026-06-25",
        status: "PLANNED",
        extensionCount: 0,
        comments: [],
        tasks: seedTasks("t-dos6", DOSSIER_TASK_LABELS, 3, "Lien Vandevelde", "2026-05-20T09:30:00.000Z"),
        deployments: [
          {
            id: "dep-7",
            dossierId: "dos-6",
            assetId: "a-005",
            expectedStart: "2026-05-20",
            expectedEnd: "2026-06-25",
            externalFirm: "Vermeer Mobiele Camerasystemen BV",
            status: "CLOSED",
            actualStart: "2026-05-20",
            actualEnd: "2026-06-25",
            executedBy: "Jeroen Vermeer",
            comments: [],
            tasks: seedTasks("t-dep7", DEPLOYMENT_TASK_LABELS, 3, "Lien Vandevelde", "2026-06-25T15:30:00.000Z"),
          },
        ],
      },
    ],
    comments: [],
    tasks: seedTasks("t-req8", REQUEST_TASK_LABELS, 3, "Lien Vandevelde", "2026-05-13T09:00:00.000Z"),
    history: [
      { id: "h-17", at: "2026-05-12T10:15:00.000Z", author: "Maarten Hendrickx", action: "Aanvraag ingediend" },
      { id: "h-18", at: "2026-05-13T09:05:00.000Z", author: "Lien Vandevelde", action: "Aanvaard, dossier aangemaakt en inzet gepland met CAM-005" },
      { id: "h-19", at: "2026-06-25T15:35:00.000Z", author: "Lien Vandevelde", action: "Ophaling geregistreerd, inzet afgerond" },
    ],
    communications: [],
    documents: [
      { id: "d-6", filename: "plaatsbezoek-kioskplaats.jpg", kind: "siteVisitPhoto", sizeLabel: "1,9 MB" },
    ],
  },
];
